import { useCallback, useEffect, useRef, useState } from "react";
import { RoomEvent } from "livekit-client";
import useVideoContext from "../useVideoContext/useVideoContext";
import { logLiveKit, LogLevel } from "utils/sentryLogger";

// iPadOS/iOS Safari can silently reject audio playback when it re-evaluates the
// audio route mid-session; the room then reports canPlaybackAudio === false and
// the only recovery is calling room.startAudio() from a user gesture. This hook
// tracks that state and provides the gesture handler.
//
// Before connect (and after disconnect) the context's `room` is a plain
// EventEmitter placeholder: `canPlaybackAudio` is undefined (reads as "not
// blocked", so no prompt appears) and `startAudio` doesn't exist, hence the
// typeof guard.
export default function useAudioPlayback() {
  const { room } = useVideoContext();
  const [canPlaybackAudio, setCanPlaybackAudio] = useState(true);

  useEffect(() => {
    const initiallyBlocked = room.canPlaybackAudio === false;
    setCanPlaybackAudio(!initiallyBlocked);
    if (initiallyBlocked) {
      // A room can already be blocked when we subscribe (e.g. strict autoplay
      // settings) — that state must reach telemetry too, not just transitions.
      logLiveKit("Audio playback blocked by browser", {
        level: LogLevel.WARNING,
        event: "audio_playback_blocked",
      });
    }
    const onChanged = () => {
      const blocked = room.canPlaybackAudio === false;
      setCanPlaybackAudio(!blocked);
      // The event fires only on real transitions, so this logs exactly once per
      // block/recover — including recoveries after the prompt has unmounted.
      logLiveKit(blocked ? "Audio playback blocked by browser" : "Audio playback recovered", {
        level: blocked ? LogLevel.WARNING : LogLevel.INFO,
        event: blocked ? "audio_playback_blocked" : "audio_playback_recovered",
      });
    };
    room.on(RoomEvent.AudioPlaybackStatusChanged, onChanged);
    return () => {
      room.off(RoomEvent.AudioPlaybackStatusChanged, onChanged);
    };
  }, [room]);

  const startAudioInFlight = useRef(false);
  const startAudio = useCallback(() => {
    if (typeof room.startAudio !== "function") return;
    // One attempt at a time: repeated gestures during a persistent block would
    // otherwise stack promises and duplicate the failure logs.
    if (startAudioInFlight.current) return;
    startAudioInFlight.current = true;
    room.startAudio().catch((e) => {
      logLiveKit("startAudio() failed", {
        level: LogLevel.WARNING,
        event: "start_audio_failed",
        errorName: e?.name,
        errorMessage: e?.message,
        roomState: room?.state,
      });
    }).finally(() => {
      startAudioInFlight.current = false;
    });
  }, [room]);

  // While blocked, ANY gesture on the page doubles as the recovery gesture —
  // students tap answer buttons constantly, so audio usually heals on the next
  // natural interaction without the prompt being needed. startAudio() must run
  // synchronously inside the gesture's dispatch for the browser to honor it;
  // capture-phase listeners make sure stopped propagation can't starve it. The
  // listeners exist only while blocked and go away on recovery.
  useEffect(() => {
    if (canPlaybackAudio || typeof room.startAudio !== "function") return;
    const onGesture = () => startAudio();
    document.addEventListener("pointerdown", onGesture, true);
    document.addEventListener("keydown", onGesture, true);
    return () => {
      document.removeEventListener("pointerdown", onGesture, true);
      document.removeEventListener("keydown", onGesture, true);
    };
  }, [canPlaybackAudio, room, startAudio]);

  return { canPlaybackAudio, startAudio };
}
